import {
  Components,
  Theme,
  boxClasses,
  buttonBaseClasses,
  buttonClasses,
  cardActionAreaClasses,
  cardActionsClasses,
  cardContentClasses,
  cardHeaderClasses,
  cardMediaClasses,
  checkboxClasses,
  radioClasses,
  typographyClasses,
} from '@mui/material';
import { ElevationBelowL, ElevationBelowS, ElevationBelowXs } from '../elevations';
import { paperClasses } from '../paper/types';
import {
  Focused, Disabled, Border, Secondary, Surface, Content,
  Brand5,
} from '../color';
import { cardClasses, miniCardClasses } from './types';
import { CornerLarge, CornerExtraLarge, CornerMedium } from '../corners';
import {
  Spacing2, Spacing3, Spacing4, Spacing5, Spacing6, Spacing7,
} from '../spacing';
import getTransition from '../utilities/getTransition';
import { Corners } from '../corners/tokens';

export const MuiCard: Components<Theme>['MuiCard'] = {
  styleOverrides: {
    root: {
      backgroundColor: Surface.variant,
      display: 'flex',
      alignItems: 'stretch',
      flexDirection: 'column',
      alignContent: 'space-between',
      overflowY: 'hidden',
      [`&.${miniCardClasses.inputRadio}, &.${miniCardClasses.inputCheckbox}, &.${miniCardClasses.inputNone}`]: {
        boxSizing: 'border-box',
        borderRadius: Corners.large,
        '&:focus-visible': {
          outline: `2px solid ${Focused.main}`,
          outlineOffset: '1px',
        },
        [`.${cardHeaderClasses.avatar}`]: {
          marginRight: Spacing3,
        },
      },

      [`&.${paperClasses.subtle}`]: {
        backgroundColor: Brand5.text,
        [`.${cardMediaClasses.root}`]: {
          marginBottom: Spacing5,
        },
        [`.${cardHeaderClasses.root}, .${cardContentClasses.root}`]: {
          paddingInlineStart: 0,
          paddingBlockStart: 0,
        },
        [`.${cardContentClasses.root}`]: {
          paddingBlockEnd: 0,
        },
      },

      [`&.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputRadio}, &.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputCheckbox}, &.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputNone}, &.${paperClasses.outlined}.${miniCardClasses.inputRadio}, &.${paperClasses.outlined}.${miniCardClasses.inputCheckbox}, &.${paperClasses.outlined}.${miniCardClasses.inputNone}, &.${paperClasses.brand}.${miniCardClasses.inputCheckbox}, &.${paperClasses.brand}.${miniCardClasses.inputRadio}, &.${paperClasses.brand}.${miniCardClasses.inputNone}`]: {
        [`&.${cardClasses.disabled}`]: {
          boxShadow: 'none',
          pointerEvents: 'none',
          '&:not(.Selected)': {
            border: 'none',
          },
          [`&.Selected.${paperClasses.brand}`]: {
            border: `2px solid ${Border.variant.main}`,
          },
          [` .${boxClasses.root}`]: {
            [` .${cardHeaderClasses.content}`]: {
              color: Disabled.default.text,
              backgroundColor: Surface.variant,
            },
          },
        },
        '&:hover': {
          ...ElevationBelowL,
          borderRadius: Corners.large,
        },
        '&:active': {
          boxShadow: 'none',
        },
        [`.${radioClasses.root}, .${checkboxClasses.root}`]: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      [`&.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputRadio}, &.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputCheckbox}, &.${paperClasses.elevation1}.${paperClasses.elevation}.${miniCardClasses.inputNone}`]: {
        ...ElevationBelowXs,
        border: '1px solid transparent',
        '&:active': {
          ...ElevationBelowXs,
          border: `1px solid ${Secondary.surface.pressed}`,
        },
        '&:hover': {
          // border: '1px solid rgba(52, 53, 61, 0.12)',
          border: `1px solid ${Secondary.surface.pressed}`,
        },
        '&.Selected': {
          border: `1px solid ${Border.primary.selected}`,
        },
      },
      [`&.${miniCardClasses.inputRadio}.Selected, &.${miniCardClasses.inputCheckbox}.Selected, &.${miniCardClasses.inputNone}.Selected`]: {
        backgroundColor: Secondary.surface.selected,
        [`&.${paperClasses.brand}`]: {
          border: `2px solid ${Border.primary.selected}`,
        },
        border: `1px solid ${Border.primary.selected}`,
        [`&.${cardClasses.disabled}`]: {
          color: Disabled.default.text,
          backgroundColor: Surface.variant,
          border: `1px solid ${Border.variant.main}`,
        },
      },
      [` .${cardActionAreaClasses.root}.${buttonBaseClasses.root}`]: {
        '&:active': {
          backgroundColor: Secondary.surface.pressed,
          borderRadius: '3px',
        },
      },
      [` .${cardActionAreaClasses.root}`]: {
        '&:hover': {
          backgroundColor: Secondary.surface.hover,
          borderRadius: '3px',
        },
      },
      [` .${cardHeaderClasses.root} .${cardHeaderClasses.content}`]: {
        color: Content.main,
      },
      [`&.${buttonBaseClasses.root}`]: {
        alignItems: 'stretch',
        textAlign: 'left',
      },
      [`&:not(.${cardClasses.disabled})`]: {
        [`.${cardContentClasses.root}`]: {
          color: Content.main,
        },
      },
      [`&.${paperClasses.root}.${paperClasses.elevation}`]: {
        ...ElevationBelowS,
      },
      [`.${cardContentClasses.root} .${typographyClasses.root}:nth-child(2)`]: {
        paddingBlockStart: Spacing5,
      },
      [`&.${cardClasses.paddingSmall}`]: {
        [`&.${cardClasses.horizontal}`]: {
          [`.${cardContentClasses.root}`]: {
            paddingInlineEnd: Spacing4,
          },
          [`.${cardHeaderClasses.root}`]: {
            paddingBlockStart: Spacing4,
            paddingInlineEnd: Spacing4,
          },
          [`.${cardActionsClasses.root}`]: {
            paddingBlockEnd: Spacing4,
            paddingInlineEnd: Spacing4,
          },
          [`&.${cardClasses.mediaPad} .${boxClasses.root} .${cardMediaClasses.root}`]: {
            paddingBlock: Spacing4,
            paddingInlineStart: Spacing4,
          },
        },
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInline: Spacing4,
          },
          [` .${cardContentClasses.root}`]: {
            paddingInline: Spacing4,
          },
          [` .${cardActionsClasses.root}`]: {
            paddingInline: Spacing4,
            paddingBlockEnd: Spacing4,
          },
          [` .${cardHeaderClasses.root}`]: {
            paddingInline: Spacing4,
            paddingBlockStart: Spacing4,
          },
        },
      },
      [`&.${cardClasses.paddingLarge}`]: {
        [`.${cardContentClasses.root}`]: {
          paddingInlineEnd: Spacing6,
        },
        [`.${cardHeaderClasses.root}`]: {
          paddingBlockStart: Spacing6,
          paddingInlineEnd: Spacing6,
        },
        [`.${cardActionsClasses.root}`]: {
          paddingBlockEnd: Spacing6,
          paddingInlineEnd: Spacing6,
        },
        [`&.${cardClasses.mediaPad} .${boxClasses.root} .${cardMediaClasses.root}`]: {
          paddingBlock: Spacing6,
          paddingInlineStart: Spacing6,
        },
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInline: Spacing6,
          },
          [` .${cardContentClasses.root}`]: {
            paddingInline: Spacing6,
          },
          [` .${cardActionsClasses.root}`]: {
            paddingInline: Spacing6,
            paddingBlockEnd: Spacing6,
          },
          [` .${cardHeaderClasses.root}`]: {
            paddingInline: Spacing6,
            paddingBlockStart: Spacing6,
          },
        },
      },
      [`&.${cardClasses.paddingExtraLarge}`]: {
        [`.${cardContentClasses.root}`]: {
          paddingInlineEnd: Spacing7,
        },
        [`.${cardHeaderClasses.root}`]: {
          paddingBlockStart: Spacing7,
          paddingInlineEnd: Spacing7,
        },
        [`.${cardActionsClasses.root}`]: {
          paddingBlockEnd: Spacing7,
          paddingInlineEnd: Spacing7,
        },
        [`&.${cardClasses.mediaPad} .${boxClasses.root} .${cardMediaClasses.root}`]: {
          paddingBlock: Spacing7,
          paddingInlineStart: Spacing7,
        },
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInline: Spacing7,
          },
          [` .${cardContentClasses.root}`]: {
            paddingInline: Spacing7,
          },
          [` .${cardActionsClasses.root}`]: {
            paddingInline: Spacing7,
            paddingBlockEnd: Spacing7,
          },
          [` .${cardHeaderClasses.root}`]: {
            paddingInline: Spacing7,
            paddingBlockStart: Spacing7,
          },
        },
      },
      [`&.${cardClasses.gapSmall}`]: {
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad}.${cardClasses.divider} .${cardMediaClasses.root}, .${cardHeaderClasses.root}, .${cardContentClasses.root}`]:
            {
              paddingBlockEnd: Spacing4,
            },
          [`.${cardContentClasses.root}`]: {
            paddingBlockStart: Spacing4,
          },
        },
        [`&.${cardClasses.horizontal}`]: {
          [`.${cardHeaderClasses.root}, .${cardContentClasses.root}, .${cardActionsClasses.root}`]:
            {
              paddingInlineStart: Spacing4,
            },
          [`&.${cardClasses.divider}.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInlineEnd: Spacing4,
          },
        },
      },
      [`&.${cardClasses.gapExtraSmall}`]: {
        [`&.${cardClasses.horizontal}`]: {
          [`.${cardHeaderClasses.root}, .${cardContentClasses.root}, .${cardActionsClasses.root}`]:
            {
              paddingInlineStart: Spacing3,
            },
          [`&.${cardClasses.divider}.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInlineEnd: Spacing3,
          },
        },
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad}.${cardClasses.divider} .${cardMediaClasses.root}, .${cardHeaderClasses.root}, .${cardContentClasses.root}`]:
            {
              paddingBlockEnd: Spacing3,
            },
          [`.${cardContentClasses.root}`]: {
            paddingBlockStart: Spacing3,
          },
        },
      },
      [`&.${cardClasses.gapLarge}`]: {
        [`&.${cardClasses.horizontal}`]: {
          [`.${cardHeaderClasses.root}, .${cardContentClasses.root}, .${cardActionsClasses.root}`]:
            {
              paddingInlineStart: Spacing6,
            },
          [`&.${cardClasses.divider}.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
            paddingInlineEnd: Spacing6,
          },
        },
        [`&:not(.${cardClasses.horizontal})`]: {
          [`&.${cardClasses.mediaPad}.${cardClasses.divider} .${cardMediaClasses.root}, .${cardHeaderClasses.root}, .${cardContentClasses.root}`]:
            {
              paddingBlockEnd: Spacing6,
            },
          [`.${cardContentClasses.root}`]: {
            paddingBlockStart: Spacing6,
          },
        },
      },
      [`&.${cardClasses.interactive}:not(.${cardClasses.disabled}), &.${buttonBaseClasses.root}`]: {
        cursor: 'pointer',
        ...getTransition(['box-shadow', 'background-color', 'color', 'border-color'], 250),
        '&:hover': {
          backgroundColor: Secondary.surface.hover,
          ...ElevationBelowL,
        },
        '&:active': {
          backgroundColor: Secondary.surface.pressed,
        },
      },
      [`&.${cardClasses.fullHeight}`]: {
        height: '100%',
      },
      [`&.${cardClasses.horizontal}`]: {
        flexDirection: 'row',
        [`&.${paperClasses.subtle} .${cardMediaClasses.root}`]: {
          marginBottom: 0,
        },
        [`.${cardContentClasses.root}`]: {
          paddingBlockStart: 0,
          [`.${typographyClasses.root}`]: {
            paddingBlockStart: 0,
          },
        },
        [`&.${paperClasses.brand}.${cardClasses.mediaPad} > :first-child`]: {
          borderRightWidth: '2px',
          borderRightColor: Border.brand.main,
          borderBottom: 0,
        },
        [`&.${cardClasses.mediaPad}.${cardClasses.divider} > :first-child`]: {
          borderRight: `1px solid ${Border.variant}`,
          borderBottom: 0,
        },

        [`&.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
          borderBottom: 0,
        },
        [`.${cardMediaClasses.root}`]: {
          borderRadius: 'inherit',
        },
      },
      [`&.${cardClasses.radiusNone}`]: {
        borderRadius: 0,
        [`&.${cardClasses.mediaPad} .${cardMediaClasses.root} > :first-child`]: {
          borderRadius: 0,
        },
      },
      [`&.${cardClasses.radiusBrand}`]: {
        ...CornerExtraLarge,
        [`&.${cardClasses.mediaPad} .${cardMediaClasses.root} > :first-child`]: {
          ...CornerLarge,
        },
      },
      [`&.${cardClasses.radiusDefault}`]: {
        ...CornerLarge,
        [`&.${cardClasses.mediaPad} .${cardMediaClasses.root} > :first-child`]: {
          ...CornerMedium,
        },
      },
      [`&.${cardClasses.mediaPad} .${cardMediaClasses.root}`]: {
        boxSizing: 'border-box',
        paddingLeft: Spacing5,
        paddingRight: Spacing5,
        paddingBottom: Spacing5,
      },
      [`&.${cardClasses.mediaPad} > .${cardMediaClasses.root}:first-child,
        &.${cardClasses.mediaPad} .MuiBox-root .${cardMediaClasses.root}
      `]: {
        paddingTop: Spacing5,
      },
      [`&.${cardClasses.disabled}`]: {
        [`.${cardContentClasses.root}, .${cardActionsClasses.root}`]: {
          color: Disabled.default.text,
        },
        [`&.${paperClasses.brand}, &.${paperClasses.brand} .${cardMediaClasses.root}`]: {
          borderColor: Border.variant.main,
        },
      },

      [`&.${paperClasses.elevation}`]: {
        ...ElevationBelowS,
      },

      [`.${cardActionAreaClasses.root}`]: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: 0,
      },

      [`.${cardContentClasses.root} + .${cardActionsClasses.root}`]: {
        paddingTop: 0,
      },

      [`&:not(.${cardClasses.divider})`]: {
        [`&:not(.${cardClasses.horizontal})`]: {
          [`.${cardMediaClasses.root}`]: {
            borderBottom: 0,
            paddingBottom: 0,
          },
        },
        [`&.${cardClasses.horizontal}`]: {
          [`.${cardMediaClasses.root}`]: {
            borderRight: 0,
            paddingRight: 0,
          },
        },
      },

      [`&:not(.${paperClasses.brand}).${cardClasses.mediaPad}.${cardClasses.divider} .${cardMediaClasses.root}`]:
        {
          borderBottom: `1px solid ${Border.variant.main}`,
          ...getTransition(['border-color'], 250),
        },
      [`&.${paperClasses.brand}.${cardClasses.divider} .${cardMediaClasses.root}`]: {
        borderBottom: `2px solid ${Border.brand.main}`,
        ...getTransition(['border-color'], 250),
      },
      [`&.${paperClasses.brand}.${cardClasses.horizontal}.${cardClasses.divider} .${cardMediaClasses.root}`]:
        {
          borderBottom: 0,
          borderRight: `2px solid ${Border.brand.main}`,
          ...getTransition(['border-color'], 250),
        },
      [`&:not(.${paperClasses.brand}).${cardClasses.horizontal}.${cardClasses.mediaPad}.${cardClasses.divider} .${cardMediaClasses.root}`]:
        {
          borderBottom: 0,
          borderRight: `1px solid ${Border.variant.main}`,
          ...getTransition(['border-color'], 250),
        },

      [`&.${cardClasses.mediaStretch} .${cardMediaClasses.root}`]: {
        border: 0,
      },

      [`&.${buttonBaseClasses.focusVisible}`]: {
        outlineColor: Focused.main,
        outlineStyle: 'solid',
        outlineWidth: 2,
        outlineOffset: 1,
      },
    },
  },
};

export const MuiCardHeader: Components<Theme>['MuiCardHeader'] = {
  styleOverrides: {
    subheader: {
      color: 'inherit',
    },
    avatar: {
      alignSelf: 'flex-start',
      paddingTop: Spacing2,
      marginRight: Spacing4,
    },
  },
  defaultProps: {
    titleTypographyProps: {
      variant: 'subtitle1',
      lineHeight: '20px',
    },
  },
};

export const MuiCardMedia: Components<Theme>['MuiCardMedia'] = {
  styleOverrides: {
    root: {
      display: 'flex',
      flexDirection: 'column',
      '> :first-child': {
        width: '100%',
      },
    },
  },
};

export const MuiCardActions: Components<Theme>['MuiCardActions'] = {
  styleOverrides: {
    root: {
      padding: Spacing5,
      marginTop: 'auto',
    },
  },
};

export const MuiCardActionArea: Components<Theme>['MuiCardActionArea'] = {
  styleOverrides: {
    root: {
      padding: 0,
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: 'transparent',
      },
      [`&.${buttonClasses.focusVisible}`]: {
        backgroundColor: 'transparent',
      },
    },
  },
};

export const OriginSelectableCard: Components<Theme>['OriginSelectableCard'] = {
  styleOverrides: undefined,
};
